/** @jsxImportSource @emotion/react */
import React from "react";
import ReactTooltip from "react-tooltip";
import Immutable from "immutable";
import {css} from "@emotion/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBringForward, faLock} from "@fortawesome/pro-regular-svg-icons";

import {betterMemo} from "js/common/utils/more-memo";

import {
  isKpiEditable,
  useCalculatedImmutableState
} from "js/admin/kpis/edit-kpis/utils";
import {getIssuesWithWrappedKpi, getNameForValidation} from "js/admin/kpis/edit-kpis/validation";
import currentClient from "js/common/repo/backbone/current-client";
import DelayedNameInput from "js/admin/kpis/edit-kpis/common/delayed-name-input";
import KpiActions from "js/admin/kpis/edit-kpis/common/kpi-actions";

const responsiveRowContainer = isCube19User => css`
    display: flex;
    align-items: center;

    input {
        @media (max-width: 1399px) {
            max-width: ${isCube19User ? "300px" : "auto"};
        }

        @media (max-width: 999px) {
            max-width: ${isCube19User ? "200px" : "auto"};
        }
    }
`;

const getMirrorTooltipContent = (kpi, kpisThatShareColumnsWithThisKpi) => <div style={{padding: "0 0.5rem"}}>
  <p>Columns on this metric are mirrored to:</p>
  <ul>
    {kpisThatShareColumnsWithThisKpi.map(kpi => {
      return <li key={kpi.get("id")}>
        {kpi.get("name")}
      </li>;
    })}
  </ul>
</div>;

const EditKpiRow = betterMemo({debug: false, displayName: "Row"}, (
    {
      clientHasEdit,
      combineError,
      handleDuplicateClick,
      handleInheritClick,
      hasChanged,
      idToTemplate,
      isCube19User,
      isEditor,
      isNameDupe,
      kpi,
      kpiIdToContributingKpis,
      kpiIdToDependentKpis,
      kpisWithMatchingColumns,
      mirrorColor,
      onDeleteMetric,
      onDisableMetricToggle,
      onKpiChange,
      onNavigationClick,
      onRevertToLegacyFormat,
      onRowClick,
      requiresExplanationUpdate,
      submitError,
      testResult,
      theme
    }) => {

  let errorLabel;
  // NOTE we pretend to have a nameToCount map here to reuse the issue generating function
  const nameToCount = new Immutable.Map({[getNameForValidation(kpi)]: isNameDupe ? 2 : 1});
  const issues = getIssuesWithWrappedKpi(new Immutable.Map({testResult, kpi, combineError, submitError}), nameToCount);
  if (!issues.isEmpty()) {
    errorLabel = issues.join(", ");
  }

  const hasNewConfig = !!kpi.get("config");
  const statusCss = {display: "none", [`@media(min-width: 1280px)`]: {display: "inline"}};
  const nonEditableTooltipId = "non-editable-" + kpi.get("id");
  const notEditableEl = (
      <div style={{marginRight: 2}}>
        <div style={{zIndex: 9999}}>
          <ReactTooltip class="cube19-dark" id={nonEditableTooltipId} effect="solid" place="left">
            <div style={{fontSize: "0.8rem", lineHeight: "1.2rem"}}>This metric cannot be edited, duplicated or
              inherited from this page.<br />
              If you need to make changes, please reach out to support.
            </div>
          </ReactTooltip>
        </div>
        <div style={{zIndex: 999}}>
          <i
              data-tip=""
              data-for={nonEditableTooltipId}
              className="bhi-non-editable"
              style={{opacity: 0.8, fontSize: "0.8rem", marginLeft: 10}} />
        </div>
      </div>
  );

  let statusEl;
  if (errorLabel) {
    statusEl = (
        <span
            style={{
              fontSize: "0.8rem",
              borderRadius: 5,
              padding: "5px 6px 5px 3px",
              marginLeft: 5,
              marginRight: 10,
              background: theme.palette.error.background,
              color: theme.palette.error.main
            }}>
          <i className="fa fa-exclamation" style={{marginLeft: "0.3rem"}} />
          <span css={statusCss}>{" " + errorLabel}</span>
        </span>);
  } else if (requiresExplanationUpdate) {
    statusEl = (
        <span
            style={{
              fontSize: "0.8rem",
              borderRadius: 5,
              padding: "5px 6px 5px 3px",
              marginLeft: 5,
              marginRight: 10,
              background: theme.palette.hints.background,
              color: theme.palette.hints.text
            }}>
          <i className="fa fa-exclamation" style={{marginLeft: "0.3rem"}} />
          <span css={statusCss}>&nbsp;Check explanation</span>
        </span>);
  } else if (hasChanged) {
    statusEl = (
        <span
            style={{
              fontSize: "0.8rem",
              borderRadius: 5,
              padding: "5px 6px 5px 3px",
              marginLeft: 5,
              marginRight: 10,
              background: theme.palette.success.background,
              color: theme.palette.success.main
            }}>
          <i className="fa fa-check" style={{marginLeft: "0.3rem"}} />
          <span css={statusCss}>&nbsp;Ready to Save</span>
        </span>);
  }
  const isLockedByOnboarding = kpi.get("createdByOnboarding")
      && currentClient.getType() !== "ENTERPRISE"
      && currentClient.getImplementationStatus() === "ONBOARDING";
  const lockTooltipId = "lock-" + kpi.get("id");

  const mirrorTooltipId = "mirror-" + kpi.get("id");
  const disabledTooltipId = "disabled-" + kpi.get("id");
  const kpisThatShareColumnsWithThisKpi = kpisWithMatchingColumns.filter(k => k.get("id") !== kpi.get("id") && !k.get(
      "deleted"));

  const allDependentKpis = kpiIdToDependentKpis.get(kpi.get("id"), Immutable.List());
  const contributingKpis = kpiIdToContributingKpis.get(kpi.get("id"), Immutable.List());
  const dependencyIds = useCalculatedImmutableState(
      () => allDependentKpis.map(kpi => kpi.get("id")),
      [kpi, allDependentKpis]);
  const handleKpiChange = React.useCallback(kpi => onKpiChange(kpi, dependencyIds), [onKpiChange, dependencyIds]);

  const hasDependencies = !allDependentKpis.isEmpty();
  const isDependent = !contributingKpis.isEmpty();
  const hasRelationships = hasDependencies || isDependent;


  const onDependencyIconClick = (e, tabId) => {
    e.stopPropagation();
    onNavigationClick(kpi.get("id"), tabId, true);
  };


  const legacyConfigMessage = "This metric cannot be edited from this page. Reach out to support for any changes that are needed.";
  const inheritAndDuplicateDisabled = !isCube19User && !isKpiEditable(kpi, idToTemplate);
  return (
      <div
          id={kpi.get("id").toString()}
          style={{margin: "0.25rem"}}
          data-test-id={"edit-kpi-row"}>
        <div
            css={css`
                background-color: ${kpi.get("visible")
                        ? theme.palette.background.card
                        : theme.palette.background.paper};
                opacity: ${kpi.get("visible") ? 1 : 0.6};
                border-radius: 4px;
                display: ${kpi.get("deleted") ? "none" : "flex"};
                align-items: center;
                padding: 5px 0 5px 5px;
                cursor: pointer;
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: ${theme.palette.primary.hover};
                }`}>
          <div
              onClick={() => onRowClick(kpi.get("id"))}
              style={{display: "flex", flexGrow: 1, marginLeft: -2, alignItems: "center"}}>
            <div css={responsiveRowContainer(isCube19User)}>
              <label
                  style={{fontSize: 10, opacity: "40%", paddingRight: "5px"}}
                  data-test-id="kpi-row-display-label">Display:</label>
              <div data-test-id={"rename-kpi-display"}>
                <DelayedNameInput
                    placeholder="Type a Metric name"
                    value={kpi.get("name")}
                    onChange={val => handleKpiChange(kpi.set("name", val.substring(0, 64)))}
                    delayInMillis={1000}
                />
              </div>
            </div>

            {isCube19User &&
                <div css={responsiveRowContainer(isCube19User)}>
                  <label style={{fontSize: 10, opacity: "40%", padding: "0 5px 0 10px"}}>Original:</label>
                  <DelayedNameInput
                      placeholder="Type a Metric name"
                      value={kpi.get("trueName")}
                      onChange={val => handleKpiChange(kpi.set("trueName", val.substring(0, 64)))}
                      delayInMillis={1000} />
                </div>
            }
            {(errorLabel || requiresExplanationUpdate || hasChanged) && <div
                css={{
                  flex: 1,
                  textAlign: "right",
                  paddingRight: 10
                }}>
              {statusEl}
            </div>
            }
          </div>
          {(!isKpiEditable(kpi, idToTemplate) && clientHasEdit && (!isCube19User || hasNewConfig)) && (
              <div css={{display: "flex", textAlign: "right", paddingRight: 10}}>
                {notEditableEl}
              </div>
          )}
          {!kpi.get("enabled") && <div style={{padding: "0 10px 0 0", display: "flex"}}>
            <div
                data-tip=""
                style={{
                  marginLeft: (hasRelationships || (isEditor && isLockedByOnboarding)) ? 10 : 0,
                  marginRight: 2,
                  marginTop: -1
                }}
                data-for={disabledTooltipId}>
              <div style={{zIndex: 9999}}>
                <ReactTooltip class="cube19-dark" id={disabledTooltipId} place="bottom" effect="solid">
                  <p style={{fontSize: 12, margin: 0}}>This metric has been disabled due to performance issues. If
                    you need help please submit a ticket.</p>
                </ReactTooltip>
              </div>
              <div style={{zIndex: 999}}>
                <i style={{color: theme.palette.error.main}} className="bhi bhi-caution-triangle-o" />
              </div>
            </div>
          </div>
          }
          {(!kpisThatShareColumnsWithThisKpi.isEmpty() || ((clientHasEdit || isEditor) && isLockedByOnboarding)) &&
              <div style={{padding: "0 10px 0 0", display: "flex"}}>
                {(clientHasEdit || isEditor) && isLockedByOnboarding &&
                    <div data-tip="" data-for={lockTooltipId} style={{marginRight: 2}}>
                      <div style={{zIndex: 9999}}>
                        <ReactTooltip class="cube19-dark" id={lockTooltipId} effect="solid" place="left">
                          <div>
                            <p>Metrics are currently locked by the Bullhorn Onboarding Team. Reach out to Bullhorn
                              Support for more details.</p>
                          </div>
                        </ReactTooltip>
                      </div>
                      <div style={{zIndex: 999}}>
                        <FontAwesomeIcon icon={faLock} />
                      </div>
                    </div>}
                {!kpisThatShareColumnsWithThisKpi.isEmpty() &&
                    <div
                        data-tip=""
                        style={{
                          marginLeft: (hasRelationships || (isEditor && isLockedByOnboarding)) ? 10 : 0,
                          marginRight: 2

                        }} data-for={mirrorTooltipId}>
                      <div style={{zIndex: 9999}}>
                        <ReactTooltip class="cube19-dark" id={mirrorTooltipId} place="bottom" effect="solid">
                          {getMirrorTooltipContent(kpi, kpisThatShareColumnsWithThisKpi)}
                        </ReactTooltip>
                      </div>
                      <div style={{zIndex: 999}}>
                        <FontAwesomeIcon
                            icon={faBringForward}
                            style={{color: mirrorColor}}
                            onClick={event => onDependencyIconClick(event, "COLUMNS")} />
                      </div>
                    </div>}
              </div>}
          <div style={{display: "flex", alignItems: "center"}}>
            <KpiActions
                kpi={kpi}
                isEditor={isEditor}
                isCube19User={isCube19User}
                clientHasEdit={clientHasEdit}
                handleKpiChange={handleKpiChange}
                onRevertToLegacyFormat={onRevertToLegacyFormat}
                onDisableMetricToggle={onDisableMetricToggle}
                onDeleteMetric={onDeleteMetric}
                handleDuplicateClick={handleDuplicateClick}
                handleInheritClick={handleInheritClick}
                inheritAndDuplicateDisabled={inheritAndDuplicateDisabled}
                legacyConfigMessage={legacyConfigMessage}
                hasNewConfig={hasNewConfig}
                variant="kebab"
            />
          </div>
        </div>
      </div>);
});


export default EditKpiRow;
